import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { differenceInDays, format, isAfter } from 'date-fns';
import { TableData } from '../../pages/IPA/OtimizacaoPrecos/types/service';
import { Store } from './optimizationPricesList';

export enum PeriodStatus {
  'weekly' = 'Semanal',
  'monthly' = 'Mensal',
}
interface Category {
  name: string;
  level: number | string;
  value: string;
}

interface Period {
  value: keyof typeof PeriodStatus;
  label: string;
}

export interface Result {
  category: Category;
  stores: Store[];
  storesIdWithoutResults: string[];
  controlStore: Store | null;
  totalProducts: number;
  period: Period;
  lastOptimization: string | null;
  nextOptimization: string;
  createdAt: string | null;
  userAlreadyAppliedAnyFilter: boolean;
  status: string;
}

interface Validity {
  start: Date;
  end?: Date | null;
}

export interface BigNumbers {
  margin: {
    optimizedValue: number;
    value: number;
    differencePercentage: number;
  };
  competitiveness: {
    optimizedValue: number;
    value: number;
    differencePercentage: number;
  };
  salesVolume: {
    optimizedValue: number;
    value: number;
    differencePercentage: number;
  };
  revenue: {
    optimizedValue: number;
    value: number;
    differencePercentage: number;
  };
  profitForecast: {
    optimizedValue: number;
    value: number;
    differencePercentage: number;
  };
  startDate: string;
  endDate: string;
}

export interface HistoryBigNumbers {
  pricesApplied: number;
  adhesion: number;
}

export type ChartForecast = {
  [key in 'base' | 'optimized']: {
    max: number;
    min: number;
    median: number;
    q1: number;
    q3: number;
  };
};

interface ForecastTab {
  bigNumbers: BigNumbers;
  chart: ChartForecast;
  filters: {
    storesSelected: string[];
  };
}

export interface ChartAnalysis {
  status: 'empty' | 'success' | null;
  dates: string[];
  haveResults: boolean;
  iraAlreadyRun: boolean;
  series: {
    name: string;
    data: number[];
    accumulated: number;
    accumulatedMonetary: number;
    variationDataMonetary: number[];
    storeId: string;
  }[];
}

export interface AnalysisTab {
  data: AnalysisData;
  filters: AnalysisFilters;
  modals: {
    howWeCalculateModal: {
      show: boolean;
    };
  };
  charts: {
    revenue: ChartAnalysis;
    profit: ChartAnalysis;
  };
}
export interface HistoryTab {
  data: HistoryData;
  filters: HistoryFilters;
  charts: HistogramChart | null;
  table: TableData[];
  pagination: HistoryTablePagination;
}

export interface HistoryTablePagination {
  page: number;
  pageSize: number;
  sortColumn: string;
  orderBy: 'asc' | 'desc';
  numberOfElements: number;
  totalPages: number;
  totalElements: number;
  size: number;
}

interface HistogramChart {
  categories: string[];
  series: {
    name: string;
    data: number[];
  }[];
}

interface AnalysisData {
  period: string[];
  periodBreakSelected: any[];
  controlStore: string;
}
interface HistoryData {
  period: string[];
  bigNumbers: HistoryBigNumbers;
}

interface AnalysisFilters {
  periodSelected: Date[] | null;
  periodBreakSelected: string;
  storesSelected: string[];
  isCompleted: boolean;
}

export type HistoryFiltersType = 'productsFamilyId' | 'productsId' | 'responsibleId';
interface HistoryFilters {
  periodSelected: Date[] | null;
  storesSelected: string[];
  isCompleted: boolean;
  productsId: string[];
  productsFamilyId: string[];
  responsibleId: string[];
}

export interface OptimizationPricesResultProps {
  header: {
    status: {
      label: string;
      active: boolean;
    };
    validity: Validity;
  };
  result: Result;
  tabs: {
    forecast: ForecastTab;
    analysis: AnalysisTab;
    history: HistoryTab;
  };
}

const initialState: OptimizationPricesResultProps = {
  header: {
    status: {
      label: '',
      active: false,
    },
    validity: {
      start: new Date(),
    },
  },
  result: {
    category: {
      name: '',
      level: '',
      value: '',
    },
    stores: [],
    storesIdWithoutResults: [],
    totalProducts: 0,
    period: {
      value: 'weekly',
      label: 'Semanal',
    },
    lastOptimization: null,
    nextOptimization: '',
    createdAt: null,
    controlStore: null,
    userAlreadyAppliedAnyFilter: false,
    status: 'ativa',
  },
  tabs: {
    forecast: {
      bigNumbers: {
        margin: {
          optimizedValue: 0,
          value: 0,
          differencePercentage: 0,
        },
        competitiveness: {
          optimizedValue: 0,
          value: 0,
          differencePercentage: 0,
        },
        salesVolume: {
          optimizedValue: 0,
          value: 0,
          differencePercentage: 0,
        },
        revenue: {
          optimizedValue: 0,
          value: 0,
          differencePercentage: 0,
        },
        profitForecast: {
          optimizedValue: 0,
          value: 0,
          differencePercentage: 0,
        },
        startDate: '',
        endDate: '',
      },
      chart: {
        base: {
          max: 0,
          min: 0,
          median: 0,
          q1: 0,
          q3: 0,
        },
        optimized: {
          max: 0,
          min: 0,
          median: 0,
          q1: 0,
          q3: 0,
        },
      },
      filters: {
        storesSelected: [],
      },
    },
    analysis: {
      modals: {
        howWeCalculateModal: {
          show: false,
        },
      },
      data: {
        controlStore: '',
        period: [],
        periodBreakSelected: [
          {
            label: 'Semanal',
            value: 'weekly',
          },
          {
            label: 'Mensal',
            value: 'monthly',
          },
        ],
      },
      filters: {
        periodSelected: [],
        periodBreakSelected: 'weekly',
        storesSelected: [],
        isCompleted: false,
      },
      charts: {
        profit: {
          status: null,
          dates: [],
          series: [],
          haveResults: false,
          iraAlreadyRun: false,
        },
        revenue: {
          status: null,
          dates: [],
          series: [],
          haveResults: false,
          iraAlreadyRun: false,
        },
      },
    },
    history: {
      pagination: {
        page: 0,
        pageSize: 10,
        sortColumn: 'appliedAt',
        orderBy: 'asc' as 'asc' | 'desc',
        numberOfElements: 0,
        totalPages: 0,
        totalElements: 0,
        size: 10,
      },
      data: {
        period: [],
        bigNumbers: {
          pricesApplied: 0,
          adhesion: 0,
        },
      },
      filters: {
        periodSelected: [],
        storesSelected: [],
        productsId: [],
        productsFamilyId: [],
        responsibleId: [],
        isCompleted: false,
      },
      charts: null,
      table: [],
    },
  },
};

function handleStatus(validity: Validity) {
  if (validity.end && isAfter(new Date(), new Date(validity.end))) {
    return {
      label: `Expirada no dia ${format(validity.end, 'dd/MM/yyyy')}`,
      active: false,
    };
  }

  if (validity.end) {
    return {
      label: `${format(validity.start, 'dd/MM/yyyy')} até ${format(validity.end, 'dd/MM/yyyy')}`,
      active: true,
    };
  }

  return {
    label: `Ativa por tempo indeterminado`,
    active: true,
  };
}
function filtersIsCompleted(filters: AnalysisFilters) {
  return !!(filters.periodSelected && filters.periodSelected?.length > 0) && filters.storesSelected?.length > 0 && filters.periodBreakSelected?.length > 0;
}

function filtersIsCompletedHistory(filters: HistoryFilters) {
  const hasStores = filters.storesSelected?.length > 0;

  const dateDiff =
    filters.periodSelected && filters.periodSelected.length === 2
      ? differenceInDays(new Date(filters.periodSelected[1]), new Date(filters.periodSelected[0]))
      : 0;

  return hasStores && dateDiff >= 7;
}

const slice = createSlice({
  name: 'optimization-prices-result',
  initialState,
  reducers: {
    setOptimizationResult(state, { payload }: PayloadAction<{ result: Result; validity: Validity }>) {
      state.result = payload.result;
      state.header.validity = payload.validity;
      state.header.status = handleStatus(payload.validity);
    },
    setOptimizationResultBigNumbers(state, { payload }: PayloadAction<{ data: BigNumbers }>) {
      state.tabs.forecast.bigNumbers = payload.data;
    },
    setSelectedStoresForecastTab(state, { payload }: PayloadAction<{ id: string[] }>) {
      state.tabs.forecast.filters.storesSelected = payload.id;
      state.result.userAlreadyAppliedAnyFilter = payload.id.length > 0;
    },
    setOptimizationResultChart(state, { payload }: PayloadAction<{ data: ChartForecast }>) {
      state.tabs.forecast.chart = payload.data;
    },
    setPeriodBreakFilter(state, { payload }: PayloadAction<{ data: string }>) {
      state.tabs.analysis.filters.periodBreakSelected = payload.data;
      state.tabs.analysis.filters.isCompleted = filtersIsCompleted(state.tabs.analysis.filters);
      state.result.userAlreadyAppliedAnyFilter = true;
    },
    setStoresIdFilter(state, { payload }: PayloadAction<{ data: string[] }>) {
      state.tabs.analysis.filters.storesSelected = payload.data;
      state.tabs.analysis.filters.isCompleted = filtersIsCompleted(state.tabs.analysis.filters);
      state.result.userAlreadyAppliedAnyFilter = true;
    },
    setPeriodFilter(state, { payload }: PayloadAction<{ data: Date[] | null }>) {
      state.tabs.analysis.filters.periodSelected = payload.data;
      state.tabs.analysis.filters.isCompleted = filtersIsCompleted(state.tabs.analysis.filters);
      state.result.userAlreadyAppliedAnyFilter = true;
    },
    setAnalysisChart(state, { payload }: PayloadAction<{ name: 'revenue' | 'profit'; data: ChartAnalysis }>) {
      state.tabs.analysis.charts[payload.name] = payload.data;
    },
    setHistoryPeriodFilter(state, { payload }: PayloadAction<{ data: Date[] | null }>) {
      state.tabs.history.filters.periodSelected = payload.data;
      state.tabs.history.filters.isCompleted = filtersIsCompletedHistory(state.tabs.history.filters);
      state.result.userAlreadyAppliedAnyFilter = true;
    },
    setHistoryStoresIdFilter(state, { payload }: PayloadAction<{ data: string[] }>) {
      state.tabs.history.filters.storesSelected = payload.data;
      state.tabs.history.filters.isCompleted = filtersIsCompletedHistory(state.tabs.history.filters);
      state.result.userAlreadyAppliedAnyFilter = true;
    },
    setHistoryFilters(state, { payload }: PayloadAction<{ filter: HistoryFiltersType; data: string[] }>) {
      state.tabs.history.filters[payload.filter] = payload.data;
      state.result.userAlreadyAppliedAnyFilter = true;
      state.tabs.history.pagination = { ...state.tabs.history.pagination, page: 0 };
    },
    setHistoryBigNumbers(state, { payload }: PayloadAction<{ data: HistoryBigNumbers }>) {
      state.tabs.history.data.bigNumbers = payload.data;
    },
    setHistoryHistogramChart(state, { payload }: PayloadAction<{ data: HistogramChart }>) {
      state.tabs.history.charts = payload.data;
    },
    setHistoryHistogramTableData(state, { payload }: PayloadAction<{ data: TableData[]; pagination: HistoryTablePagination }>) {
      state.tabs.history.table = payload.data;
      state.tabs.history.pagination = { ...state.tabs.history.pagination, ...payload.pagination };
    },
    setHistoryPagination(
      state,
      {
        payload,
      }: PayloadAction<{
        pagination: Partial<HistoryTablePagination>;
      }>,
    ) {
      state.tabs.history.pagination = { ...state.tabs.history.pagination, ...payload.pagination };
    },
    resetHistoryPagination(state) {
      state.tabs.history.pagination = { ...initialState.tabs.history.pagination };
    },
    setShowHowWeCalculateModal(state, { payload }: PayloadAction<{ show: boolean }>) {
      state.tabs.analysis.modals.howWeCalculateModal.show = payload.show;
    },
    applySharedFiltersToHistoryAndAnalysis(
      state,
      { payload }: PayloadAction<{ periodSelected: Date[] | null; historyStoresSelected: string[]; analysisStoresSelected: string[] }>,
    ) {
      state.tabs.history.filters.periodSelected = payload.periodSelected;
      state.tabs.forecast.filters.storesSelected = payload.analysisStoresSelected;
      state.tabs.history.filters.storesSelected = payload.historyStoresSelected;
      state.tabs.history.filters.isCompleted = filtersIsCompletedHistory(state.tabs.history.filters);

      state.tabs.analysis.filters.periodSelected = payload.periodSelected;
      state.tabs.analysis.filters.storesSelected = payload.analysisStoresSelected.filter(storeId => storeId !== state.result.controlStore?.storeId);
      state.tabs.analysis.filters.isCompleted = filtersIsCompleted(state.tabs.analysis.filters);
    },
    resetFiltersOptimizationResult(state) {
      state.result.userAlreadyAppliedAnyFilter = false;
      state.tabs.history.filters.periodSelected = [];
      state.tabs.history.filters.storesSelected = [];
      state.tabs.history.filters.isCompleted = false;

      state.tabs.analysis.filters.periodSelected = [];
      state.tabs.analysis.filters.storesSelected = [];
      state.tabs.analysis.filters.isCompleted = false;

      state.tabs.forecast.filters.storesSelected = [];
    },
    resetAnalysis(state) {
      state.tabs.analysis = initialState.tabs.analysis;
    },
  },
});

export const {
  setOptimizationResult,
  setOptimizationResultBigNumbers,
  setSelectedStoresForecastTab,
  setOptimizationResultChart,
  setPeriodBreakFilter,
  setStoresIdFilter,
  setPeriodFilter,
  setAnalysisChart,
  resetFiltersOptimizationResult,
  resetAnalysis,
  setHistoryPeriodFilter,
  setHistoryStoresIdFilter,
  setHistoryFilters,
  setHistoryBigNumbers,
  setHistoryHistogramChart,
  setHistoryHistogramTableData,
  setShowHowWeCalculateModal,
  applySharedFiltersToHistoryAndAnalysis,
  setHistoryPagination,
  resetHistoryPagination,
} = slice.actions;

export default slice.reducer;
